"use client";

import * as Dialog from "@radix-ui/react-dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuTrigger,
} from "@ui/components/dropdown-menu";
import { cn } from "@ui/lib";
import { XIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import {
	type ReactElement,
	type ReactNode,
	createContext,
	useContext,
	useRef,
} from "react";
import { useMediaQuery } from "usehooks-ts";

/** 底部面板里没有 Radix Menu，选项要换成普通按钮；值是关面板的方法，下拉里为 null */
const SheetCloseContext = createContext<(() => void) | null>(null);

/**
 * 工作台的模型卡：手机从底部弹面板，和配置条 GenerationSettings 一样；
 * 768 以上是下拉，≥1024 往右边展开（10/25 老公定，照 AIReel）
 */
export function ModelPicker({
	open,
	onOpenChange,
	trigger,
	title,
	listClassName,
	children,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	trigger: ReactElement;
	/** 面板的无障碍标题，和 GenerationSettings 一样不显示 */
	title: string;
	listClassName?: string;
	children: ReactNode;
}) {
	const t = useTranslations();
	const isMobile = useMediaQuery("(max-width: 767px)", {
		initializeWithValue: false,
	});
	const isLargeScreen = useMediaQuery("(min-width: 1024px)", {
		initializeWithValue: false,
	});
	const triggerPointerRef = useRef<string | null>(null);

	if (isMobile) {
		return (
			<Dialog.Root open={open} onOpenChange={onOpenChange}>
				<Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
				<Dialog.Portal>
					<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
					<Dialog.Content
						aria-describedby={undefined}
						className="fixed inset-x-0 bottom-0 z-50 flex max-h-[calc(100svh-1rem)] flex-col rounded-t-3xl bg-white shadow-xl outline-none duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom"
					>
						<div className="flex shrink-0 items-center justify-end px-3">
							<Dialog.Title className="sr-only">
								{title}
							</Dialog.Title>
							<Dialog.Close className="flex size-11 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
								<XIcon className="size-5" />
								<span className="sr-only">
									{t("shared.actions.close")}
								</span>
							</Dialog.Close>
						</div>
						<div
							className={cn(
								"min-h-0 overflow-y-auto overscroll-contain px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]",
								listClassName,
							)}
						>
							<SheetCloseContext.Provider
								value={() => onOpenChange(false)}
							>
								{children}
							</SheetCloseContext.Provider>
						</div>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>
		);
	}

	return (
		<DropdownMenu open={open} onOpenChange={onOpenChange}>
			{/* Radix 手指一按下就打开，触屏上按着模型卡往上滑页面会被当成点开、页面滑不动（10/19 边界测试查到）。
			    触屏改成抬手（click）才开，滑动不会触发 click；鼠标照旧。手机走上面的面板，Dialog 本来就是 click 才开 */}
			<DropdownMenuTrigger
				asChild
				onPointerDown={(event) => {
					triggerPointerRef.current = event.pointerType;
					if (event.pointerType === "touch") {
						event.preventDefault();
					}
				}}
				onClick={() => {
					if (triggerPointerRef.current === "touch") {
						onOpenChange(!open);
					}
					triggerPointerRef.current = null;
				}}
			>
				{trigger}
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="start"
				side={isLargeScreen ? "right" : "bottom"}
				sideOffset={isLargeScreen ? 12 : 4}
				className={cn(
					"w-[var(--radix-dropdown-menu-trigger-width)] min-w-[300px] p-2",
					listClassName,
				)}
			>
				{children}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

/** 付费墙要盖在上面时，onSelect 里 event.preventDefault() 让下拉 / 面板别关 */
export function ModelPickerItem({
	onSelect,
	disabled,
	className,
	children,
}: {
	onSelect: (event: Event) => void;
	disabled?: boolean;
	className?: string;
	children: ReactNode;
}) {
	const close = useContext(SheetCloseContext);
	if (!close) {
		return (
			<DropdownMenuItem
				disabled={disabled}
				onSelect={onSelect}
				className={className}
			>
				{children}
			</DropdownMenuItem>
		);
	}
	return (
		<button
			type="button"
			disabled={disabled}
			onClick={(event) => {
				onSelect(event.nativeEvent);
				if (!event.nativeEvent.defaultPrevented) {
					close();
				}
			}}
			className={cn(
				"relative flex w-full select-none items-center rounded-sm px-3 py-2 text-left text-sm transition-colors active:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:pointer-events-none disabled:opacity-50",
				className,
			)}
		>
			{children}
		</button>
	);
}

export function ModelPickerLabel({
	className,
	children,
}: {
	className?: string;
	children: ReactNode;
}) {
	const close = useContext(SheetCloseContext);
	if (!close) {
		return (
			<DropdownMenuLabel className={className}>
				{children}
			</DropdownMenuLabel>
		);
	}
	return (
		<div className={cn("px-3 py-2 font-semibold text-sm", className)}>
			{children}
		</div>
	);
}
